import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'; // createAction non serve se usi i reducers di createSlice
import { IProject, IAASHTOResults, IGeogridData } from '../../../api/IApiResponse';
import { RootState, IInitialState } from '../../../../reduxService';
import { EProjectType, unit as unitEnum } from '../../../enum/project';
import { v4 as uuidv4 } from 'uuid'; // Utile per avere un default guid se non passato
import { ProjectData, IContainerYardData, IParkingDeckData } from '../../../api/IApiResponse'; // Assicurati che IContainerYardData sia importato
import { t } from 'i18next';
import * as ProjectTypeService from '../../../services/ProjectTypeService';
import { logDebug, LogTag } from '../../../utils/utils';
import GoalSeekService from '../../../services/GoalSeekService';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { OptimizedExcelCellCalculatorService } from '../../../services/OptimizedExcelCellCalculatorService';
import { materialDatabaseService } from '../../../services/MaterialDatabaseService';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DrainageCoefficientService } from '../../../services/DrainageCoefficientService';
import * as ProjectApiService from '../../../services/ProjectApiService';
import { isNestedStructure } from '../../../types/ProjectDataStructure';
import { markGeogridSafetyFactorInputVersion } from '../../../services/GeogridSafetyFactor';

// Payload type for the new action
interface InitializeNewProjectPayload {
    guid: string;
    type: EProjectType;
    method: string;
    unit: unitEnum;
    newProject: boolean;
    // Aggiungi altri campi iniziali se necessario, es. projectName?
    // projectName?: string;
}

const initialState: IInitialState<IProject> = {
    initialState: true,
    data: undefined,
};

// Azione sincrona interna per l'aggiornamento del draft
const _updateProjectDraftSyncReducer = (state: IInitialState<IProject>, action: PayloadAction<IProject>) => {
    state.data = action.payload;
    state.initialState = false;
    // Assicurati che newProject sia true per i draft
    if (state.data) {
        state.data.newProject = true;
    }
};

// Funzione Helper per creare/aggiornare projectInfo.data in modo type-safe
const createOrUpdateProjectSpecificData = (
    newOverallProjectType: EProjectType, // Tipo generale del progetto dal payload
    currentProjectInfoData: ProjectData | undefined, // projectInfo.data esistente
    defaultSpecificProjectTypeForData: EProjectType // Ora accetta qualsiasi valore di EProjectType
): ProjectData => {
    let targetSpecificType: EProjectType;
    if (
        newOverallProjectType === EProjectType.container_yard ||
        newOverallProjectType === EProjectType.parking_deck ||
        newOverallProjectType === EProjectType.paved ||
        newOverallProjectType === EProjectType.airport ||
        newOverallProjectType === EProjectType.unpaved ||
        newOverallProjectType === EProjectType.railways ||
        newOverallProjectType === EProjectType.geogrid
    ) {
        targetSpecificType = newOverallProjectType;
    } else {
        targetSpecificType = defaultSpecificProjectTypeForData;
    }
    const commonNotes = currentProjectInfoData?.notes || '';
    const commonDesignerFirmCity = (currentProjectInfoData as any)?.designerFirmCity || '';
    const commonNrProject = (currentProjectInfoData as any)?.nrProject || '';
    const commonGeoGrid = currentProjectInfoData?.geoGrid;

    if (targetSpecificType === EProjectType.container_yard) {
        // FLAT structure (PD3/PD4): fields at root level, same pattern as Paved
        // Handles both already-flat data (from Redux) and legacy NESTED data (with bcr/static keys)
        const existingData = currentProjectInfoData?.type === EProjectType.container_yard ? (currentProjectInfoData as any) : null;
        const currentMethod = existingData?.method || 'bcr';
        const methodData = existingData?.[currentMethod];
        return {
            type: EProjectType.container_yard,
            method: currentMethod,
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            load: existingData?.load || methodData?.load || {},
            subgradeCharacteristics: existingData?.subgradeCharacteristics || methodData?.subgradeCharacteristics || {},
            embankmentCharacteristics: existingData?.embankmentCharacteristics || methodData?.embankmentCharacteristics || {},
            subgrade: existingData?.subgrade || methodData?.subgrade || {},
            fsDesign: existingData?.fsDesign || methodData?.fsDesign || {},
        } as ProjectData;
    } else if (targetSpecificType === EProjectType.parking_deck) {
        // FLAT structure (PD3/PD4): fields at root level, same pattern as Paved
        const existingData = currentProjectInfoData?.type === EProjectType.parking_deck ? (currentProjectInfoData as any) : null;
        const currentMethod = existingData?.method || 'bcr';
        const methodData = existingData?.[currentMethod];
        return {
            type: EProjectType.parking_deck,
            method: currentMethod,
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            load: existingData?.load || methodData?.load || {},
            subgradeCharacteristics: existingData?.subgradeCharacteristics || methodData?.subgradeCharacteristics || {},
            embankmentCharacteristics: existingData?.embankmentCharacteristics || methodData?.embankmentCharacteristics || {},
            subgrade: existingData?.subgrade || methodData?.subgrade || {},
            fsDesign: existingData?.fsDesign || methodData?.fsDesign || {},
        } as ProjectData;
    } else if (targetSpecificType === EProjectType.paved) {
        // FLAT structure: fields directly at root level, method indicates formula type
        const existingData = currentProjectInfoData?.type === EProjectType.paved ? (currentProjectInfoData as any) : null;
        const existingCalculatedThickness = existingData?.calculatedDesignThickness;
        return {
            type: EProjectType.paved,
            method: existingData?.method || 'asphalt',
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            // FLAT: fields directly at root, NOT nested under asphalt
            roadInfo: existingData?.roadInfo || existingData?.asphalt?.roadInfo || {},
            trafficLoad: existingData?.trafficLoad || existingData?.asphalt?.trafficLoad || {},
            baseCourse: existingData?.baseCourse || existingData?.asphalt?.baseCourse || {},
            subbaseCourse: existingData?.subbaseCourse || existingData?.asphalt?.subbaseCourse || {},
            subgrade: existingData?.subgrade || existingData?.asphalt?.subgrade || {},
            ...(existingCalculatedThickness && { calculatedDesignThickness: existingCalculatedThickness }),
        };
    } else if (targetSpecificType === EProjectType.airport) {
        // FLAT structure: same as Paved, fields directly at root level
        const existingData = currentProjectInfoData?.type === EProjectType.airport ? (currentProjectInfoData as any) : null;
        const existingCalculatedThickness = existingData?.calculatedDesignThickness;
        return {
            type: EProjectType.airport,
            method: existingData?.method || 'asphalt',
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            // FLAT: fields directly at root, NOT nested under asphalt
            roadInfo: existingData?.roadInfo || existingData?.asphalt?.roadInfo || {},
            trafficLoad: existingData?.trafficLoad || existingData?.asphalt?.trafficLoad || {},
            baseCourse: existingData?.baseCourse || existingData?.asphalt?.baseCourse || {},
            subbaseCourse: existingData?.subbaseCourse || existingData?.asphalt?.subbaseCourse || {},
            subgrade: existingData?.subgrade || existingData?.asphalt?.subgrade || {},
            ...(existingCalculatedThickness && { calculatedDesignThickness: existingCalculatedThickness }),
        };
    } else if (targetSpecificType === EProjectType.unpaved) {
        // FLAT structure: method indicates formula (giroud/leng), data at root level
        const existingData = currentProjectInfoData?.type === EProjectType.unpaved ? (currentProjectInfoData as any) : null;
        return {
            type: EProjectType.unpaved,
            method: existingData?.method || 'giroud',
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            // FLAT: preserve existing flat fields or extract from nested giroud/leng
            subgrade: existingData?.subgrade || existingData?.giroud?.subgrade || existingData?.leng?.subgrade || {},
            coverThickness: existingData?.coverThickness || existingData?.giroud?.coverThickness || existingData?.leng?.coverThickness,
            trafficLoad: existingData?.trafficLoad || existingData?.giroud?.trafficLoad || existingData?.leng?.trafficLoad || {},
        };
    } else if (targetSpecificType === EProjectType.railways) {
        // Sprint 0 (AC2): Railway uses FLAT structure only (v5.1.0+)
        // REMOVED: NESTED fallback - transformProjectFromBackend() guarantees FLAT
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const railwayData = currentProjectInfoData as any;

        return {
            type: EProjectType.railways,
            notes: commonNotes,
            designerFirmCity: commonDesignerFirmCity,
            nrProject: commonNrProject,
            geoGrid: commonGeoGrid,
            // FLAT structure: fields directly on currentProjectInfoData
            railwayType: railwayData?.railwayType || {},
            loadData: railwayData?.loadData || {},
            trackGeometry: railwayData?.trackGeometry || {},
            trafficData: railwayData?.trafficData || {},
            soilProperties: railwayData?.soilProperties || {},
            poissonRatios: railwayData?.poissonRatios || {},
            materialTypes: railwayData?.materialTypes || {},
            designParameters: railwayData?.designParameters || {},
            ...(railwayData?.calculatedDesignThickness && { calculatedDesignThickness: railwayData.calculatedDesignThickness }),
        };
    }
    return {
        type: EProjectType.container_yard, // Default fallback
        notes: commonNotes,
        designerFirmCity: commonDesignerFirmCity,
        nrProject: commonNrProject,
        geoGrid: commonGeoGrid,
    };
};

export const projectSlice = createSlice({
    name: 'dynamicProject',
    initialState,
    reducers: {
        cleanProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        setProject: (state, action: PayloadAction<IProject>) => {
            // Sprint 0 (AC2): Validate that incoming data is FLAT, not NESTED
            // This catches bugs where NESTED backend response is accidentally stored in Redux
            if (process.env.NODE_ENV === 'development') {
                const payload = action.payload;
                const projectData = payload?.projectInfo?.data;
                const method = payload?.method;
                if (projectData && method && isNestedStructure(projectData, method, payload?.type)) {
                    console.error(
                        '[projectslice.setProject] ⚠️ NESTED structure detected! Redux MUST store FLAT only.',
                        { method, type: payload?.type, dataKeys: Object.keys(projectData) }
                    );
                }
            }
            state.initialState = false;
            state.data = action.payload;
        },

        initializeNewProject: (state, action: PayloadAction<InitializeNewProjectPayload>) => {
            const currentData = state.data;
            const payload = action.payload;
            const now = new Date().toISOString();
            const defaultSpecificProjectTypeForData = EProjectType.geogrid; // Default specific type for projectInfo.data

            if (!currentData && payload.newProject) {
                const initialProjectSpecificData = createOrUpdateProjectSpecificData(
                    payload.type,
                    undefined, // Nessun currentProjectInfoData per un nuovo progetto
                    defaultSpecificProjectTypeForData
                );

                state.data = {
                    guid: payload.guid,
                    type: payload.type,
                    unit: payload.unit,
                    // Initialize conversion tracking for new projects
                    originalUnit: payload.unit,
                    conversionApplied: false,
                    conversionSource: 'new_project',
                    method: payload.method,
                    newProject: true,
                    projectName: '',
                    mapImage: '',
                    readOnly: false,
                    hasBeenRan: false,
                    idApp: Number(process.env.REACT_APP_ID_APP) || 42,
                    idUser: 0,
                    creationDate: now,
                    lastModified: now,
                    country: '',
                    language: '',
                    status: 0,
                    isCalculated: false,
                    isValid: false,
                    isApproved: false,
                    lastSeen: now,
                    sharedUsers: [],
                    geogridHasBeenRan: false,
                    geogridUserModified: false,
                    disableRun: false,
                    projectInfo: {
                        coordinates: [44.5215, 11.2833],
                        projectOwner: '',
                        owner: '',
                        designerFirmName: '',
                        designerFirmCountry: '',
                        productCountry: undefined,
                        data: initialProjectSpecificData,
                    },
                };
                state.initialState = false;
                return;
            }

            if (!currentData) {
                state.error = {
                    status: -1,
                    data: 'Cannot set project type: previous project details are missing.',
                };
                state.initialState = true;
                state.data = undefined;
                return;
            }

            // ✅ FIX: Preserve designerFirmCity and nrProject from currentData
            // This ensures these fields aren't lost when switching project types
            const preservedDesignerFirmCity = (currentData.projectInfo?.data as any)?.designerFirmCity || '';
            const preservedNrProject = (currentData.projectInfo?.data as any)?.nrProject || '';

            const updatedProjectSpecificData = createOrUpdateProjectSpecificData(payload.type, currentData.projectInfo.data, defaultSpecificProjectTypeForData);

            // Project Info creates the draft before its first Railway type selection.
            // Initialize here: empty groups at form load also occur in incomplete saved projects.
            if (
                currentData.newProject &&
                currentData.type !== EProjectType.railways &&
                currentData.projectInfo.data.type !== EProjectType.railways &&
                updatedProjectSpecificData.type === EProjectType.railways
            ) {
                const { schema } = ProjectTypeService.resolveProjectSchemas(payload.type, payload.method);
                const ballastType = schema?.fields.materialTypes?.find(field => field.name === 'ballastType')?.defaultValue;
                const cbrOfBallast = schema?.fields.soilProperties?.find(field => field.name === 'cbrOfBallast')?.defaultValue;
                if (typeof ballastType === 'string' && typeof cbrOfBallast === 'number') {
                    updatedProjectSpecificData.materialTypes = { ...updatedProjectSpecificData.materialTypes, ballastType };
                    updatedProjectSpecificData.soilProperties = { ...updatedProjectSpecificData.soilProperties, cbrOfBallast };
                }
            }

            state.data = {
                ...currentData,
                guid: payload.guid,
                type: payload.type,
                unit: payload.unit,
                method: payload.method,
                newProject: payload.newProject !== undefined ? payload.newProject : currentData.newProject,
                lastModified: now,
                projectInfo: {
                    ...currentData.projectInfo,
                    data: {
                        ...updatedProjectSpecificData,
                        // ✅ Explicitly preserve common properties
                        designerFirmCity: preservedDesignerFirmCity,
                        nrProject: preservedNrProject,
                    },
                },
            };
            state.initialState = false;
        },

        _updateProjectDraftSync: (state, action: PayloadAction<IProject>) => {
            _updateProjectDraftSyncReducer(state, action);
        },
        updateProjectDetails: (state, action: PayloadAction<Partial<IProject>>) => {
            if (state.data) {
                // 🔧 FIX: Deep merge projectInfo.data to preserve common fields (immutable)
                // REASON: Shallow spread overwrites projectInfo.data completely, losing designerFirmCity/nrProject
                // Create new immutable payload object instead of mutating action.payload
                let updatedPayload = action.payload;
                if (action.payload.projectInfo && state.data.projectInfo) {
                    // Deep merge projectInfo.data if both exist
                    if (action.payload.projectInfo.data && state.data.projectInfo.data) {
                        updatedPayload = {
                            ...action.payload,
                            projectInfo: {
                                ...action.payload.projectInfo,
                                data: {
                                    ...state.data.projectInfo.data, // ← Preserve existing fields
                                    ...action.payload.projectInfo.data, // ← Merge new fields
                                } as any, // Type-cast needed for ProjectData union compatibility
                            },
                        };
                    }
                }

                state.data = {
                    ...state.data,
                    ...updatedPayload,
                    // Set newProject to false when updating details (unless explicitly provided in payload)
                    newProject: updatedPayload.newProject !== undefined ? updatedPayload.newProject : false,
                    lastModified: new Date().toISOString(),
                };
            } else {
                // Se non ci sono dati, usa i valori del payload (compreso newProject se fornito)
                state.data = {
                    ...(action.payload as IProject),
                    lastModified: new Date().toISOString(),
                };
            }
            state.initialState = false;
        },
        setReadOnly: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.readOnly = action.payload;
        },
        setHasBeenRan: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.hasBeenRan = action.payload;
        },
        setIsNavigatingToResults: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.isNavigatingToResults = action.payload;
        },
        // 🔧 FIX: Update design enable flags immediately in Redux when toggle changes
        // This allows Results_Paved to see the updated values without waiting for form submission
        updateDesignEnableFlags: (state, action: PayloadAction<{ section: 'surfaceDesign' | 'baseDesign' | 'subbaseDesign'; enabled: 'yes' | 'no' }>) => {
            if (state.data?.projectInfo?.data) {
                const { section, enabled } = action.payload;
                const sectionData = (state.data.projectInfo.data as any)[section];

                if (sectionData) {
                    if (section === 'surfaceDesign') {
                        sectionData.enableSurfaceDesign = enabled;
                    } else if (section === 'baseDesign') {
                        sectionData.enableBaseDesign = enabled;
                    } else if (section === 'subbaseDesign') {
                        sectionData.enableSubbaseDesign = enabled;
                    }
                }
            }
        },
        setGeogridHasBeenRan: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.geogridHasBeenRan = action.payload;
        },
        setGeogridUserModified: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.geogridUserModified = action.payload;
        },
        setDisableRun: (state, action: PayloadAction<boolean>) => {
            if (state.data) state.data.disableRun = action.payload;
        },
        updateFormFields: (state, action: PayloadAction<Record<string, any>>) => {
            if (state.data) {
                // Preserva gli altri dati in state.data, aggiorna solo dynamicFields
                state.data.formFields = action.payload;
            } else {
            }
        },
        setGeoGridData: (state, action: PayloadAction<IGeogridData>) => {
            // Safely check if the necessary nested path exists
            if (state.data?.projectInfo?.data) {
                // Replace the existing geogrid data with the new data from the payload. Every write comes from the current
                // form or calculation, so it carries the FS input version (never migrated on the next load).
                state.data.projectInfo.data.geoGrid = markGeogridSafetyFactorInputVersion(action.payload);

                // Update the last modified timestamp for the project
                state.data.lastModified = new Date().toISOString();
            } else {
                // Log a warning if the path doesn't exist (shouldn't normally happen if project is loaded)
                // Optionally set an error state?
                // state.error = { status: -3, data: "Cannot update GeoGrid data, project structure missing." };
            }
        },
        clearGeoGridData: state => {
            if (state.data?.projectInfo?.data) {
                // Use delete operator to remove the property
                delete state.data.projectInfo.data.geoGrid;
                state.data.lastModified = new Date().toISOString();
                // Reset user modification flag when clearing data
                state.data.geogridUserModified = false;
            }
        },
        setAASHTOResults: (state, action: PayloadAction<IAASHTOResults>) => {
            if (state.data) {
                state.data.aashtoResults = action.payload;
                state.data.lastModified = new Date().toISOString();
            }
        },
        setPavedCalculatedDesignThickness: (
            state,
            action: PayloadAction<{
                selectedDesignType: 'surface' | 'base' | 'subbase' | 'standard';
                thickness: { surface: number; base: number; subbase: number };
                isStabilised: boolean;
            }>
        ) => {
            if (
                state.data?.projectInfo?.data &&
                'type' in state.data.projectInfo.data &&
                (state.data.projectInfo.data.type === EProjectType.paved || state.data.projectInfo.data.type === EProjectType.airport)
            ) {
                const projectData = state.data.projectInfo.data as import('../../../api/IApiResponse').IPavedData | import('../../../api/IApiResponse').IAirportData;
                projectData.calculatedDesignThickness = {
                    selectedDesignType: action.payload.selectedDesignType,
                    thickness: action.payload.thickness,
                    isStabilised: action.payload.isStabilised,
                    calculatedAt: Date.now(),
                };
                state.data.lastModified = new Date().toISOString();
                logDebug('ProjectSlice', `Set calculated design thickness for ${action.payload.selectedDesignType} design:`, action.payload.thickness, LogTag.REDUX_STATE);
            } else {
            }
        },
        // Reset conversion state tracking for manual reset or cleanup
        resetConversionState: state => {
            if (state.data) {
                state.data.originalUnit = state.data.unit;
                state.data.conversionApplied = false;
                state.data.conversionSource = 'reset';
                state.data.lastModified = new Date().toISOString();
            }
        },
    },
});

export const saveProjectDraft = createAsyncThunk<
    string, // Tipo del valore ritornato (GUID)
    Partial<IProject>, // Tipo dell'argomento del thunk (dati del progetto parziali)
    { state: RootState } // Tipi opzionali per getState e dispatch
>('dynamicProject/saveProjectDraft', async (projectDataFromForm, { dispatch, getState }) => {
    const newGuid = projectDataFromForm.guid || uuidv4(); // Genera GUID se non fornito
    const now = new Date().toISOString();
    const state = getState();
    const currentData = state.Dynamic.project?.data;

    // Get the existing projectInfo.data to preserve its structure
    const currentProjectInfoData = currentData?.projectInfo?.data;
    const incomingProjectInfoData = projectDataFromForm.projectInfo?.data;

    // Create or update project-specific data, preserving existing structure
    const updatedProjectInfoData =
        currentProjectInfoData && incomingProjectInfoData
            ? createOrUpdateProjectSpecificData(
                  projectDataFromForm.type || currentData?.type || EProjectType.geogrid,
                  { ...currentProjectInfoData, ...incomingProjectInfoData } as ProjectData,
                  EProjectType.geogrid
              )
            : createOrUpdateProjectSpecificData(projectDataFromForm.type || EProjectType.geogrid, incomingProjectInfoData as ProjectData, EProjectType.geogrid);

    // Costruisci l'oggetto IProject completo, assicurandoti che tutti i campi obbligatori siano presenti
    const fullProjectData: IProject = {
        guid: newGuid,
        projectName: projectDataFromForm.projectName || '',
        // nrProject: projectDataFromForm.nrProject || '', // Remove this line - moving to projectInfo.data
        type: projectDataFromForm.type || EProjectType.geogrid,
        method: projectDataFromForm.method || '',
        unit: projectDataFromForm.unit || unitEnum.SI,
        mapImage: projectDataFromForm.mapImage || '',
        projectInfo: {
            coordinates: projectDataFromForm.projectInfo?.coordinates || [44.5215, 11.2833],
            projectOwner: projectDataFromForm.projectInfo?.projectOwner || '', // Access from nested projectInfo
            owner: projectDataFromForm.projectInfo?.owner || '', // Also set owner for compatibility
            designerFirmName: projectDataFromForm.projectInfo?.designerFirmName || '', // designerFirmName is already correctly positioned in projectInfo
            designerFirmCountry: projectDataFromForm.projectInfo?.designerFirmCountry || '', // Access from nested projectInfo
            productCountry: projectDataFromForm.projectInfo?.productCountry || undefined, // productCountry is already correctly positioned in projectInfo
            products: (projectDataFromForm.projectInfo?.products as string[]) || [],
            data: {
                ...updatedProjectInfoData,
                designerFirmCity: (projectDataFromForm.projectInfo?.data as any)?.designerFirmCity || '', // Access from nested projectInfo.data
                nrProject: (projectDataFromForm.projectInfo?.data as any)?.nrProject || '', // Move nrProject here
            } as ProjectData,
        },
        readOnly: projectDataFromForm.readOnly || false,
        hasBeenRan: projectDataFromForm.hasBeenRan || false,
        idApp: projectDataFromForm.idApp || Number(process.env.REACT_APP_ID_APP) || 42,
        idUser: projectDataFromForm.idUser || 0,
        creationDate: projectDataFromForm.creationDate || now,
        lastModified: now,
        country: projectDataFromForm.country || '',
        language: projectDataFromForm.language || '',
        status: projectDataFromForm.status || 0,
        isCalculated: projectDataFromForm.isCalculated || false,
        isValid: projectDataFromForm.isValid || false,
        isApproved: projectDataFromForm.isApproved || false,
        lastSeen: now,
        sharedUsers: projectDataFromForm.sharedUsers || [],
        geogridHasBeenRan: projectDataFromForm.geogridHasBeenRan || false,
        geogridUserModified: projectDataFromForm.geogridUserModified || false,
        disableRun: projectDataFromForm.disableRun || false,
        newProject: true, // Fondamentale per i nuovi draft
    };

    dispatch(projectSlice.actions._updateProjectDraftSync(fullProjectData));
    return newGuid; // Ritorna il GUID
});

/**
 * Salva il progetto corrente sul backend
 * Usa PUT /api/Project/projects/{guid}
 * - Se il progetto non esiste, viene creato (201)
 * - Se esiste già, viene aggiornato (200)
 */
export const saveProjectToBackend = createAsyncThunk<
    { success: boolean; status: 200 | 201; guid: string }, // Return type
    void, // No payload needed - uses current Redux state
    { state: RootState; rejectValue: string } // Config with rejectValue for error handling
>('dynamicProject/saveProjectToBackend', async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const currentProject = state.Dynamic.project?.data;

    if (!currentProject) {
        logDebug('ProjectSlice', '❌ Cannot save: No project data in Redux state');
        return rejectWithValue('No project data available to save');
    }

    logDebug('ProjectSlice', `Saving project to backend: ${currentProject.projectName}`, { guid: currentProject.guid });

    const result = await ProjectApiService.saveProjectToBackend(currentProject);

    if (result.success && (result.status === 200 || result.status === 201)) {
        logDebug('ProjectSlice', `✅ Project saved successfully (${result.status})`, { guid: currentProject.guid });
        return { success: true, status: result.status, guid: currentProject.guid };
    } else {
        logDebug('ProjectSlice', `❌ Failed to save project: ${result.error}`);
        return rejectWithValue(result.error || 'Failed to save project');
    }
});

/**
 * Carica tutti i progetti dell'utente dal backend
 * Usa GET /api/Project/projects
 */
export const loadProjectsFromBackend = createAsyncThunk<
    IProject[], // Return type: array of frontend projects
    void, // No payload
    { rejectValue: string } // Error handling
>('dynamicProject/loadProjectsFromBackend', async (_, { rejectWithValue }) => {
    logDebug('ProjectSlice', 'Loading all projects from backend');

    const result = await ProjectApiService.loadProjectsFromBackend();

    if (result.success && result.data) {
        // Transform backend projects to frontend format
        const frontendProjects = result.data.map(backendProject => ProjectApiService.transformProjectFromBackend(backendProject));

        logDebug('ProjectSlice', `✅ Loaded ${frontendProjects.length} projects from backend`);
        return frontendProjects;
    } else {
        logDebug('ProjectSlice', `❌ Failed to load projects: ${result.error}`);
        return rejectWithValue(result.error || 'Failed to load projects');
    }
});

/**
 * Carica un singolo progetto dal backend per GUID
 * Usa GET /api/Project/projects/{guid}
 */
export const loadProjectByGuid = createAsyncThunk<
    IProject, // Return type: single frontend project
    string, // Payload: guid
    { rejectValue: { error: string; status?: number } } // Error handling with status
>('dynamicProject/loadProjectByGuid', async (guid, { rejectWithValue }) => {
    logDebug('ProjectSlice', `Loading project by GUID: ${guid}`);

    const result = await ProjectApiService.loadProjectByGuid(guid);

    if (result.success && result.data) {
        const frontendProject = ProjectApiService.transformProjectFromBackend(result.data);
        logDebug('ProjectSlice', `✅ Loaded project: ${frontendProject.projectName}`);
        return frontendProject;
    } else {
        logDebug('ProjectSlice', `❌ Failed to load project: ${result.error}`);
        return rejectWithValue({ error: result.error || 'Project not found', status: result.status });
    }
});

/**
 * Elimina un progetto dal backend
 * Usa DELETE /api/Project/projects/{guid}
 */
export const deleteProjectFromBackend = createAsyncThunk<
    string, // Return type: deleted GUID
    string, // Payload: guid to delete
    { rejectValue: { error: string; status?: number } } // Error handling
>('dynamicProject/deleteProjectFromBackend', async (guid, { rejectWithValue }) => {
    logDebug('ProjectSlice', `Deleting project: ${guid}`);

    const result = await ProjectApiService.deleteProjectFromBackend(guid);

    if (result.success) {
        logDebug('ProjectSlice', `✅ Project deleted: ${guid}`);
        return guid;
    } else {
        logDebug('ProjectSlice', `❌ Failed to delete project: ${result.error}`);
        return rejectWithValue({ error: result.error || 'Failed to delete project', status: result.status });
    }
});

// Aggiorna gli export
export const {
    initializeNewProject,
    setProject,
    cleanProject,
    _updateProjectDraftSync,
    updateProjectDetails,
    setReadOnly,
    setHasBeenRan,
    setIsNavigatingToResults,
    updateDesignEnableFlags,
    setGeogridHasBeenRan,
    setGeogridUserModified,
    setDisableRun,
    updateFormFields,
    setGeoGridData,
    clearGeoGridData,
    setAASHTOResults,
    setPavedCalculatedDesignThickness,
    resetConversionState,
} = projectSlice.actions;

export default projectSlice.reducer;

export const selectDynamicProject = (state: RootState) => state.Dynamic.project;
